import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { color, Icon, spacing, Typography } from '@uniquegood/realworld-studio-design';
import { onMobile } from '@/styles/responsive';
import PlanBadge from '@/components/PlanBadge';
import { getInteractiveTechDefinition } from '../helpers';

type TemplateDetailModalProps = {
  title: string;
  description: string;
  additionalDescription: string;
  imageUrl: ProjectTemplateResponseModel['imageUrl'];
  usedTechs: ProjectTemplateResponseModel['usedTechnologis'];
  plans: ProjectTemplateResponseModel['availablePlans'];
  previewImages: ProjectTemplateResponseModel['previewImageUrls'];
};

export function TemplateDetailModal({
  title,
  description,
  additionalDescription,
  imageUrl,
  usedTechs,
  plans,
  previewImages
}: TemplateDetailModalProps) {
  return (
    <div
      css={css`
        height: auto;
        max-height: 456px;
      `}
    >
      <div css={templateDetailsHeader}>
        <img src={imageUrl ?? ''} alt="템플릿 섬네일 사진" />
        <dl>
          {plans.length > 0 && (
            <div css={templatePlanBadgeContainer}>
              {plans.map((plan) => (
                <PlanBadge key={plan} plan={plan} />
              ))}
            </div>
          )}
          <Typography
            as="dt"
            type="displaySmall"
            cssStyle={css`
              margin-bottom: ${spacing.margin.small};
            `}
          >
            {title}
          </Typography>
          <Typography as="dd" type="body" cssStyle={templateDetailsDescription}>
            {description}
          </Typography>
          <Typography as="dd" type="body" cssStyle={templateDetailsAdditionalDescription}>
            {additionalDescription}
          </Typography>
        </dl>
      </div>
      <Typography
        type="heading"
        cssStyle={css`
          margin-bottom: ${spacing.margin.medium};
        `}
      >
        사용하는 실감 기술
      </Typography>
      {usedTechs.length > 0 && (
        <InteractiveTechList>
          {usedTechs.map((tech) => {
            const techDefinition = getInteractiveTechDefinition(tech);

            if (!techDefinition) {
              return null;
            }

            return (
              <InteractiveTechCardWrapper key={tech}>
                <InteractiveTechCard>
                  <Icon
                    icon={techDefinition.icon}
                    size="32px"
                    color={techDefinition.type === 'basic' ? color.icon_subdued : ''}
                  />
                </InteractiveTechCard>
                <Typography type="caption" cssStyle={interactiveTechLabelStyle}>
                  {techDefinition.label}
                </Typography>
              </InteractiveTechCardWrapper>
            );
          })}
        </InteractiveTechList>
      )}
      {previewImages.length > 0 && (
        <div>
          <Typography
            as="h3"
            type="heading"
            cssStyle={css`
              margin-bottom: ${spacing.margin.large};
            `}
          >
            콘텐츠 미리보기
          </Typography>
          <PreviewImageList>
            {previewImages.map((image) => (
              <img key={image} src={image} alt="미리보기 이미지" width={602} />
            ))}
          </PreviewImageList>
        </div>
      )}
    </div>
  );
}

const PreviewImageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.margin.large};
`;

const InteractiveTechList = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: ${spacing.margin.xlarge2};

  /* 가로 스크롤 활성화 */
  overflow-x: auto;
  overflow-y: hidden;

  /* 스크롤바 숨기기 (모든 브라우저) */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE, Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const InteractiveTechCardWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 10px;
  width: 80px;
  flex-shrink: 0;
`;

const InteractiveTechCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background-color: ${color.surface_default_hovered};
  border-radius: ${spacing.borderRadius.medium};
`;

const templatePlanBadgeContainer = css`
  display: flex;
  gap: 4px;
  margin-bottom: ${spacing.margin.small};

  ${onMobile} {
    margin-top: ${spacing.margin.small};
  }
`;

const templateDetailsHeader = css`
  display: flex;
  margin-bottom: ${spacing.margin.xlarge2};

  img {
    width: 240px;
    height: 180px;

    object-fit: cover;

    border-radius: ${spacing.borderRadius.xlarge};
    margin-right: ${spacing.margin.large};
  }

  dl {
    align-self: center;

    ${onMobile} {
      align-self: initial;
    }
  }

  ${onMobile} {
    flex-direction: column;
  }
`;

const templateDetailsDescription = css`
  margin-bottom: ${spacing.margin.small};
  overflow-wrap: anywhere;
  align-self: center;

  ${onMobile} {
    margin: ${spacing.margin.small} 0;
  }
`;

const templateDetailsAdditionalDescription = css`
  overflow-wrap: anywhere;
  white-space: pre-line;

  max-height: 216px;
  overflow-y: auto;

  ${onMobile} {
    max-height: initial;
    overflow-y: initial;
  }
`;

const interactiveTechLabelStyle = css`
  text-align: center;
  word-break: keep-all;
  line-height: 1.3;
`;
